import { ThemeOptions } from '@mui/material'
import { ThemeModeProvider } from 'fwork-react-mui-theme-mode-provider'
import { RequiredAuthWrapperComponent } from 'fwork.react.apptemplate'
import { SnackbarProvider } from 'notistack'
import React from 'react'
import { Provider, useSelector } from 'react-redux'
import { RouterProvider, createHashRouter } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { LoadingBarLinearComponent } from './components/common/progressBar'
import { RoutesNames, onLoginPayloadOverride } from './globals'
import { AuthorizationPage } from './pages/authorization'
import { AuthorizationsPage } from './pages/authorizations'
import { CompaniesPage } from './pages/companies'
import { CompanyPage } from './pages/company'
import { EventPage } from './pages/event'
import { EventsPage } from './pages/events'
import { HomePage } from './pages/home'
import { LocationPage } from './pages/location'
import { LocationsPage } from './pages/locations'
import { LoginPage } from './pages/login'
import { LogsPage } from './pages/logs'
import { LogsClassificationPage } from './pages/logsClassification'
import { LogsClassificationsPage } from './pages/logsClassifications'
import { ParticipantPage } from './pages/participant'
import { ParticipantsPage } from './pages/participants'
import { SportPage } from './pages/sport'
import { SportsPage } from './pages/sports'
import { DbTaskPage } from './pages/task'
import { DbTasksPage } from './pages/tasks'
import { TournamentPage } from './pages/tournament'
import { TournamentsPage } from './pages/tournaments'
import { UserPage } from './pages/user'
import { UserGroupPage } from './pages/userGroup'
import { UsersPage } from './pages/users'
import { UsersGroupsPage } from './pages/usersGroups'
import { IDotEnvProductionState } from './redux/reducers/dotenvProduction.slice'
import { RootState, persistor, store } from './redux/store'
import { CompaniesGroupsPage } from './pages/comapniesGroups'
import { CompanyGroupPage } from './pages/companyGroup'
import { ScoreTypesPage } from './pages/scoreTypes'
import { ScoreTypePage } from './pages/scoreType'

const RequiredAuthWrapperComponentExtended = (props: {
  children?: React.ReactNode
}) => {
  const dotEnvProductionState: IDotEnvProductionState = useSelector((state: RootState) => state.dotEnvProductionState)

  return <RequiredAuthWrapperComponent loginOptions={{
    apiUrl: `${dotEnvProductionState.payload?.APIURL}/auth`,
    onPayloadOverride: onLoginPayloadOverride,
  }}>
    {props.children}
  </RequiredAuthWrapperComponent>
}

const router = createHashRouter([
  {
    path: RoutesNames.home,
    element: <RequiredAuthWrapperComponentExtended>
      <HomePage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.login,
    element: <LoginPage />
  },
  {
    path: RoutesNames.sports,
    element: <RequiredAuthWrapperComponentExtended>
      <SportsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.sport,
    element: <RequiredAuthWrapperComponentExtended>
      <SportPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.tournaments,
    element: <RequiredAuthWrapperComponentExtended>
      <TournamentsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.tournament,
    element: <RequiredAuthWrapperComponentExtended>
      <TournamentPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.participants,
    element: <RequiredAuthWrapperComponentExtended>
      <ParticipantsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.participant,
    element: <RequiredAuthWrapperComponentExtended>
      <ParticipantPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.events,
    element: <RequiredAuthWrapperComponentExtended>
      <EventsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.event,
    element: <RequiredAuthWrapperComponentExtended>
      <EventPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.users,
    element: <RequiredAuthWrapperComponentExtended>
      <UsersPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.user,
    element: <RequiredAuthWrapperComponentExtended>
      <UserPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.locations,
    element: <RequiredAuthWrapperComponentExtended>
      <LocationsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.location,
    element: <RequiredAuthWrapperComponentExtended>
      <LocationPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.usersGroups,
    element: <RequiredAuthWrapperComponentExtended>
      <UsersGroupsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.userGroup,
    element: <RequiredAuthWrapperComponentExtended>
      <UserGroupPage />
    </RequiredAuthWrapperComponentExtended>
  },

  {
    path: RoutesNames.companiesGroups,
    element: <RequiredAuthWrapperComponentExtended>
      <CompaniesGroupsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.companyGroup,
    element: <RequiredAuthWrapperComponentExtended>
      <CompanyGroupPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.companies,
    element: <RequiredAuthWrapperComponentExtended>
      <CompaniesPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.company,
    element: <RequiredAuthWrapperComponentExtended>
      <CompanyPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.tasks,
    element: <RequiredAuthWrapperComponentExtended>
      <DbTasksPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.task,
    element: <RequiredAuthWrapperComponentExtended>
      <DbTaskPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.authorizations,
    element: <RequiredAuthWrapperComponentExtended>
      <AuthorizationsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.authorization,
    element: <RequiredAuthWrapperComponentExtended>
      <AuthorizationPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.logsClassifications,
    element: <RequiredAuthWrapperComponentExtended>
      <LogsClassificationsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.logsClassification,
    element: <RequiredAuthWrapperComponentExtended>
      <LogsClassificationPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.logs,
    element: <RequiredAuthWrapperComponentExtended>
      <LogsPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.scoreTypes,
    element: <RequiredAuthWrapperComponentExtended>
      <ScoreTypesPage />
    </RequiredAuthWrapperComponentExtended>
  },
  {
    path: RoutesNames.scoreType,
    element: <RequiredAuthWrapperComponentExtended>
      <ScoreTypePage />
    </RequiredAuthWrapperComponentExtended>
  },
])

const themeDark: ThemeOptions = {
  palette: {
    primary: {
      main: '#5893df',
    },
    secondary: {
      main: '#2ec5d3',
    },
    background: {
      default: '#192231',
      paper: '#24344d',
    },
  }
}

export const App = () => {
  return <SnackbarProvider maxSnack={3} preventDuplicate={true} autoHideDuration={2000}
    anchorOrigin={{
      horizontal: 'right',
      vertical: 'top'
    }}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {/* <MetaPixelComponent /> */}
        <ThemeModeProvider
          themeOptionsDark={themeDark}
          boxProps={{
            style: {
              height: '100%'
            }
          }}
        >
          {/* <CookiePolicyComponent /> */}
          <LoadingBarLinearComponent />
          <RouterProvider router={router} />
        </ThemeModeProvider>
      </PersistGate>
    </Provider>
  </SnackbarProvider>
}